'use client';

import type { FC } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import type { GameAssetDto } from '@/queries/media/media.schema';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { AppImage } from '@/components/ui/AppImage/AppImage';
import { Dialog, DialogDescription, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/Dialog/dialog';
import { cn } from '@/utils/cn';
import { GameTabsMap } from '../utils/detailed-game.utils';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

interface GameGalleryProps {
  className?: string;
  assets: GameAssetDto[];
}

const swiperPropsDefault = {
  slidesPerView: 'auto' as const,
  freeMode: true,
  modules: [FreeMode],
  spaceBetween: 24,
  slidesOffsetBefore: 16,
  slidesOffsetAfter: 16,

  breakpoints: {
    1024: {
      slidesOffsetBefore: 40,
      slidesOffsetAfter: 40,
    },
    1178: {
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
    },
  },
};

export const GameGallery: FC<GameGalleryProps> = ({ className, assets }) => {
  const t = useTranslations('game');
  const [isOpen, setIsOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});

  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  const handleOpen = (index: number) => {
    setInitialSlide(index);
    setIsOpen(true);

    const assetId = assets[index]?.id;
    if (assetId && !loadingImages[assetId]) {
      setLoadingImages(prev => ({ ...prev, [assetId]: true }));
    }
  };

  const handleImageLoad = (assetId: string) => {
    setLoadingImages(prev => ({ ...prev, [assetId]: false }));
  };

  const handleImageError = (assetId: string) => {
    setLoadingImages(prev => ({ ...prev, [assetId]: false }));
  };

  const handleModalClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    const isImage = target.tagName === 'IMG' || target.closest('img');
    const isNavigationButton = target.closest('.swiper-button-next, .swiper-button-prev, button[data-swiper-nav]');

    if (!isImage && !isNavigationButton) {
      setIsOpen(false);
    }
  };

  const handleLightboxSwiperInit = (swiper: SwiperType) => {
    if (
      prevRef.current
      && nextRef.current
      && swiper.params.navigation
      && typeof swiper.params.navigation === 'object'
    ) {
      (swiper.params.navigation as any).prevEl = prevRef.current;
      (swiper.params.navigation as any).nextEl = nextRef.current;
      swiper.navigation.init();
      swiper.navigation.update();
    }
  };

  const handleSwiperInit = (swiper: SwiperType) => {
    setShowLeftGradient(!swiper.isBeginning);
    setShowRightGradient(!swiper.isEnd);
  };

  const handleSlideChange = (swiper: SwiperType) => {
    setShowLeftGradient(!swiper.isBeginning);
    setShowRightGradient(!swiper.isEnd);
  };

  if (!assets?.length) {
    return null;
  }

  return (
    <div id={GameTabsMap.gallery.id} className={cn('lg:max-w-221.25 3xl:max-w-242 relative w-full mb-8.5 md:mb-12', className)}>
      <h3 className="text-xl leading-[1.3] font-medium tracking-normal text-light">
        {t('game-details.gallery')}
      </h3>
      <div className="relative -mx-4 lg:-ml-10 lg:-mr-10 min-[1178px]:mr-0! min-[1178px]:ml-0! overflow-hidden lg:overflow-visible">
        {/* Left gradient */}
        {showLeftGradient && (
          <div className="absolute -left-0.5 top-0 bottom-0 w-6 bg-linear-to-r from-c900 to-transparent z-5 pointer-events-none transition-opacity duration-300" />
        )}

        {/* Right gradient */}
        {showRightGradient && (
          <div className="absolute -right-0.5 top-0 bottom-0 w-6 bg-linear-to-l from-c900 to-transparent z-5 pointer-events-none transition-opacity duration-300" />
        )}

        <Swiper
          {...swiperPropsDefault}
          onSwiper={handleSwiperInit}
          onSlideChange={handleSlideChange}
          onReachBeginning={() => setShowLeftGradient(false)}
          onReachEnd={() => setShowRightGradient(false)}
          className="py-4! [&:not(.swiper-initialized)_.swiper-slide]:mr-6"

        >
          {assets.map((asset, index) => (
            <SwiperSlide
              key={asset.id}
              className="cursor-pointer w-fit! h-auto!"
              onClick={() => handleOpen(index)}
            >
              <div className="relative h-82.5 w-50 overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-transform hover:scale-[1.02]">
                <AppImage
                  src={asset.media.key ?? asset.media.path}
                  alt={asset.media.meta?.alt || `Game screenshot ${index + 1}`}
                  variant="gallery"
                  className="h-full w-full"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogPortal data-slot="dialog-portal">
          <DialogOverlay className="bg-black/50 backdrop-blur-lg fixed top-0 left-0 right-0 bottom-0 grid place-items-center">
            <DialogPrimitive.Content
              data-slot="dialog-content"
              className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 z-50 mx-4 my-20 duration-200',
                'max-w-[90vw] w-full h-[80vh] border-none bg-transparent p-0 flex flex-col items-center justify-center',
                'focus-within:right-transparent focus:outline-none',
              )}
              style={{ outline: 'none' }}
              onClick={handleModalClick}
            >
              <VisuallyHidden.Root>
                <DialogTitle className="sr-only">
                  {t('game-details.gallery')}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t('game-details.gallery')}
                </DialogDescription>
              </VisuallyHidden.Root>
              <div className="relative w-full h-full">

                <button
                  type="button"
                  ref={prevRef}
                  data-swiper-nav="prev"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-black/50 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-all duration-200 group"
                  aria-label="Previous image"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>

                <button
                  type="button"
                  ref={nextRef}
                  data-swiper-nav="next"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-black/50 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-all duration-200 group"
                  aria-label="Next image"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>

                {isOpen && (
                  <Swiper
                    initialSlide={initialSlide}
                    modules={[Navigation, Thumbs]}
                    navigation={false}
                    spaceBetween={20}
                    slidesPerView={1}
                    className="w-full h-full"
                    onSwiper={handleLightboxSwiperInit}
                  >
                    {assets.map(asset => (
                      <SwiperSlide key={`lightbox-${asset.id}`} className="flex! items-center justify-center">
                        <div className="relative max-w-full max-h-full flex items-center justify-center">
                          {/* Loader */}
                          {loadingImages[asset.id] && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                              <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                            </div>
                          )}

                          <img
                            src={asset.media.path}
                            alt={asset.media.meta?.alt || 'Game screenshot'}
                            className={cn(
                              'object-contain max-w-full max-h-[80vh] w-auto h-auto transition-opacity duration-300',
                              loadingImages[asset.id] ? 'opacity-0' : 'opacity-100',
                            )}
                            width={1920}
                            height={1080}
                            sizes="90vw"
                            onLoad={() => handleImageLoad(asset.id)}
                            onError={() => handleImageError(asset.id)}
                            onLoadStart={() => setLoadingImages(prev => ({ ...prev, [asset.id]: true }))}
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
              </div>
            </DialogPrimitive.Content>
          </DialogOverlay>
        </DialogPortal>

      </Dialog>
    </div>
  );
};
